import { defineStore } from 'pinia';
import { Me } from '@/api/interfaces/me';
import { loadCurrentUser } from '@/api';
import { useCompaniesStore } from './companies';

interface CurrentUserState {
  user: Me | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
}

export const useCurrentUserStore = defineStore('currentUser', {
  state: (): CurrentUserState => ({
    user: null,
    isLoading: false,
    isLoaded: false,
    error: null,
  }),

  getters: {
    userId: (state): string | null => {
      return state.user?.id || null;
    },

    isAuthenticated: (state): boolean => {
      return state.user !== null;
    },

    userDisplayName: (state): string => {
      return state.user?.display_name || '';
    },

    userAvatar: (state): string => {
      return state.user?.avatar_url || '';
    },

    userRole: (state): string => {
      return state.user?.role?.name || '';
    },

    userTitle: (state): string => {
      return state.user?.title || '';
    },

    /** Feature flags из /me (objections_mining, personal_cabinet, …) */
    features: (state): Record<string, boolean> => {
      return state.user?.features ?? {};
    },

    /** Ключ вертикали тенанта (данные из /me) */
    tenantVertical: (state): string => {
      return state.user?.vertical ?? 'hr';
    },

    /**
     * Вертикаль разрешает модуль возражений (серверный контракт vertical_features).
     * Если хеша ещё нет (старый /me) — fallback: только hr.
     */
    verticalAllowsObjections: (state): boolean => {
      const vf = state.user?.vertical_features;
      if (vf != null && Object.keys(vf).length > 0) {
        return vf['objections'] === true;
      }
      return (state.user?.vertical ?? 'hr') === 'hr';
    },

    /**
     * Возражения в продукте: тариф (objections_mining) и вертикаль (vertical_features).
     */
    objectionsProductEnabled: (state): boolean => {
      if (!state.user?.features?.objections_mining) {
        return false;
      }
      const vf = state.user?.vertical_features;
      if (vf != null && Object.keys(vf).length > 0) {
        return vf['objections'] === true;
      }
      return (state.user?.vertical ?? 'hr') === 'hr';
    },

    hasError: (state): boolean => {
      return state.error !== null;
    },

    needsLoading: (state): boolean => {
      return !state.isLoaded && !state.isLoading;
    },
  },

  actions: {
    canLoadUser(): boolean {
      const companiesStore = useCompaniesStore();
      return companiesStore.hasSelectedCompany;
    },

    async loadUser(): Promise<void> {
      if (this.isLoading) {
        return;
      }

      if (!this.canLoadUser()) {

        this.error = 'Требуется выбор компании';
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {

        const userData = await loadCurrentUser();
        this.user = userData;
        this.isLoaded = true;

      } catch (error) {
        console.error('Failed to load current user:', error);
        this.error = error instanceof Error ? error.message : 'Неизвестная ошибка';
        this.user = null;
      } finally {
        this.isLoading = false;
      }
    },

    async loadUserAfterCompanySelection(): Promise<void> {

      this.isLoaded = false;
      await this.loadUser();
    },

    async refreshUser(): Promise<void> {
      this.isLoaded = false;
      await this.loadUser();
    },

    clearUser(): void {
      this.user = null;
      this.isLoaded = false;
      this.error = null;
    },

    clearError(): void {
      this.error = null;
    },

    updateUser(userData: Partial<Me>): void {
      if (this.user) {
        Object.assign(this.user, userData);
      }
    },
  },
}); 